import axios,{AxiosResponse} from "axios"
import {tokenName, role, id, fullname, email, subscriptionStatus, subscriptionExpiry} from "./data"
import {MeUrl,UsersUrl,CasesUrl,CaseStatisticsUrl,CaseOutcomeAnalyticsUrl,QLetterCasesUrl,InfoForLetterCasesUrl,CaseScoringQuestionsUrl,CategoryWeightagesUrl,LikelihoodScoresUrl} from "./network"
import { AuthTokenType,UserType,CaseRecordProps,CaseCreationResponse} from "./types"


export const getAuthToken= ():AuthTokenType|null=>{
    const accessToken =localStorage.getItem(tokenName)
    if(!accessToken){
        return null
    }

    return {headers:{Authorization:`Bearer ${accessToken}`}}

}

export const logout =()=>{
    localStorage.removeItem(tokenName)
    localStorage.removeItem(role)
    localStorage.removeItem(id)
    localStorage.removeItem(fullname)
    localStorage.removeItem(email)
    localStorage.removeItem(subscriptionStatus)
    localStorage.removeItem(subscriptionExpiry)
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?redirect=${redirectUrl}`;
    } else {
        window.location.href = "/login";
    }
}

export const authHandler=async ():Promise<UserType | null>=>{
    const headers=getAuthToken()
        if(!headers){
            return null
    }
    const response:AxiosResponse =await axios.get(MeUrl,headers).catch(
        (e)=>{}
    ) as AxiosResponse
    if(response && response.data){
        if (response.data.subscription_status) {
            localStorage.setItem(subscriptionStatus, response.data.subscription_status);
        } else {
            localStorage.setItem(subscriptionStatus, 'inactive');
        }
        if (response.data.role) {
            localStorage.setItem(role, response.data.role);
        }
        if (response.data.id !== undefined) {
            localStorage.setItem(id, response.data.id.toString());
        }
        if (response.data.fullname) {
            localStorage.setItem(fullname, response.data.fullname);
        }
        if (response.data.email) {
            localStorage.setItem(email, response.data.email);
        }
        const expiry = response.data.subscription_expiry_date || response.data.expiry_date;
        if (expiry) {
            localStorage.setItem(subscriptionExpiry, expiry);
        } else {
            localStorage.removeItem(subscriptionExpiry);
        }
        return response.data as UserType
    }
    return null

}

export const checkIsSubscribed = (status?: string | null, expiry?: string | null, isAdmin: boolean = false): boolean => {
    if (isAdmin) return true;
    const currentStatus = status || localStorage.getItem(subscriptionStatus) || 'inactive';
    const currentExpiry = expiry || localStorage.getItem(subscriptionExpiry) || '';

    if (currentStatus === 'active' || currentStatus === 'trialing') {
        return true;
    }

    if (currentStatus === 'past_due') {
        if (!currentExpiry) return true;
        const expiryDate = new Date(currentExpiry);
        if (isNaN(expiryDate.getTime())) return true;
        
        const now = new Date();
        if (expiryDate > now) return true;

        const gracePeriodEnd = new Date(expiryDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        return gracePeriodEnd > now;
    }

    return false;
};


export const getUsers = async (
  setUsers: (data: any) => void,
  setFetching: (val: boolean) => void
) => {
  const headers = getAuthToken() as AuthTokenType;
  setFetching(true);

  try {
    const response: AxiosResponse = await axios.get(UsersUrl, headers);
    
    setUsers(response.data);
  } finally {
    setFetching(false);
  }
};

// Case Management Functions
export const getCases = async (
  setCases: (data: CaseRecordProps[]) => void,
  setFetching: (val: boolean) => void
) => {
  const headers = getAuthToken() as AuthTokenType;
  setFetching(true);

  try {
    const response: AxiosResponse = await axios.get(CasesUrl, headers);
    setCases(response.data.results || response.data);
  } catch (error) {
    console.error('Error fetching cases:', error);
    setCases([]);
  } finally {
    setFetching(false);
  }
};

export const getQLetterCases = async (
  setCases: (data: CaseRecordProps[]) => void,
  setFetching: (val: boolean) => void
) => {
  const headers = getAuthToken() as AuthTokenType;
  setFetching(true);

  try {
    const response: AxiosResponse = await axios.get(QLetterCasesUrl, headers);
    setCases(response.data.results || response.data);
  } catch (error) {
    console.error('Error fetching Q-Letter cases:', error);
    setCases([]);
  } finally {
    setFetching(false);
  }
};

export const getInfoForLetterCases = async (
  setCases: (data: CaseRecordProps[]) => void,
  setFetching: (val: boolean) => void
) => {
  const headers = getAuthToken() as AuthTokenType;
  setFetching(true);

  try {
    const response: AxiosResponse = await axios.get(InfoForLetterCasesUrl, headers);
    setCases(response.data.results || response.data);
  } catch (error) {
    console.error('Error fetching InfoForLetter cases:', error);
    setCases([]);
  } finally {
    setFetching(false);
  }
};

export const createCase = async (caseData: Partial<CaseRecordProps>): Promise<CaseCreationResponse | null> => {
  const headers = getAuthToken() as AuthTokenType;
  
  try {
    const response: AxiosResponse = await axios.post(CasesUrl, caseData, headers);
    return response.data;
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
};

export const updateCase = async (caseId: number, caseData: Partial<CaseRecordProps>): Promise<CaseRecordProps | null> => {
  const headers = getAuthToken() as AuthTokenType;
  
  try {
    const response: AxiosResponse = await axios.put(`${CasesUrl}${caseId}/`, caseData, headers);
    return response.data;
  } catch (error) {
    console.error('Error updating case:', error);
    throw error;
  }
};

export const deleteCase = async (caseId: number): Promise<boolean> => {
  const headers = getAuthToken() as AuthTokenType;
  
  try {
    await axios.delete(`${CasesUrl}${caseId}/`, headers);
    return true;
  } catch (error) {
    console.error('Error deleting case:', error);
    throw error;
  }
};

export const promoteCase = async (caseId: number, prevailValue: string): Promise<CaseRecordProps | null> => {
  const headers = getAuthToken() as AuthTokenType;
  
  try {
    const response: AxiosResponse = await axios.post(
      `${CasesUrl}${caseId}/promote_to_info_for_letter/`, 
      { prevail: prevailValue }, 
      headers
    );
    return response.data;
  } catch (error) {
    console.error('Error promoting case:', error);
    throw error;
  }
};

export const getCaseStatistics = async (): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.get(CaseStatisticsUrl, headers);
    return response.data;
  } catch (error) {
    console.error('Error fetching case statistics:', error);
    throw error;
  }
};

export const getCaseOutcomeAnalytics = async (): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.get(CaseOutcomeAnalyticsUrl, headers);
    return response.data;
  } catch (error) {
    console.error('Error fetching case outcome analytics:', error);
    throw error;
  }
};

// Scoring Questions Functions
export const getScoringQuestions = async (
  setQuestions: (data: any) => void,
  setFetching: (val: boolean) => void,
  filters?: { category?: string; case_type?: string; basis?: string }
) => {
  const headers = getAuthToken();
  if (!headers) {
    setFetching(false);
    return;
  }
  
  setFetching(true);
  try {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.case_type) params.append('case_type', filters.case_type);
    if (filters?.basis) params.append('basis', filters.basis);
    
    const url = params.toString() 
      ? `${CaseScoringQuestionsUrl}?${params.toString()}`
      : CaseScoringQuestionsUrl;
    
    const response: AxiosResponse = await axios.get(url, headers);
    setQuestions(response.data);
  } catch (error) {
    console.error('Error fetching scoring questions:', error);
  } finally {
    setFetching(false);
  }
};

export const populateScoringQuestions = async (overwrite: boolean = false): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.post(
      `${CaseScoringQuestionsUrl}populate_default/`,
      { overwrite },
      headers
    );
    return response.data;
  } catch (error) {
    console.error('Error populating scoring questions:', error);
    throw error;
  }
};

export const updateScoringQuestion = async (id: number, data: { score?: number; description?: string; order?: number; answer?: string }): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.patch(
      `${CaseScoringQuestionsUrl}${id}/`,
      data,
      headers
    );
    return response.data;
  } catch (error) {
    console.error('Error updating scoring question:', error);
    throw error;
  }
};

// Algorithm Settings Functions
export const getCategoryWeightages = async (
  setWeightages: (data: any[]) => void,
  setFetching: (val: boolean) => void
): Promise<void> => {
  const headers = getAuthToken();
  if (!headers) {
    setFetching(false);
    return;
  }
  
  setFetching(true);
  try {
    const response: AxiosResponse = await axios.get(CategoryWeightagesUrl, headers);
    setWeightages(response.data);
  } catch (error) {
    console.error('Error fetching category weightages:', error);
  } finally {
    setFetching(false);
  }
};

export const updateCategoryWeightage = async (id: number, weightage: number): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.patch(
      `${CategoryWeightagesUrl}${id}/`,
      { weightage },
      headers
    );
    return response.data;
  } catch (error) {
    console.error('Error updating category weightage:', error);
    throw error;
  }
};

export const getLikelihoodScores = async (
  setScores: (data: any[]) => void,
  setFetching: (val: boolean) => void
): Promise<void> => {
  const headers = getAuthToken();
  if (!headers) {
    setFetching(false);
    return;
  }
  
  setFetching(true);
  try {
    const response: AxiosResponse = await axios.get(LikelihoodScoresUrl, headers);
    setScores(response.data);
  } catch (error) {
    console.error('Error fetching likelihood scores:', error);
  } finally {
    setFetching(false);
  }
};

export const updateLikelihoodScore = async (id: number, data: { min_range?: number; max_range?: number }): Promise<any> => {
  const headers = getAuthToken();
  if (!headers) {
    throw new Error('Authentication required');
  }
  
  try {
    const response: AxiosResponse = await axios.patch(
      `${LikelihoodScoresUrl}${id}/`,
      data,
      headers
    );
    return response.data;
  } catch (error) {
    console.error('Error updating likelihood score:', error);
    throw error;
  }
};












